import React, { useEffect, useState } from "react";
import { patchUser } from "../../api";
import { handleApiError } from "../../utils/apiErrorHandler";
import {
  ProfileAlert,
  ProfileCard,
  ProfileCardDesc,
  ProfileCardHead,
  ProfileCardIcon,
  ProfileCardTitle,
  ProfileField,
  ProfileFields,
  ProfileFooter,
  ProfileGhostBtn,
  ProfileHint,
  ProfileInput,
  ProfileLabel,
  ProfilePrimaryBtn,
} from "./Profile.styles";

const KeyIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="m15.5 7.5 2.3 2.3a1 1 0 0 0 1.4 0l2.1-2.1a1 1 0 0 0 0-1.4L19 4" />
    <path d="m21 2-9.6 9.6" />
    <circle cx="7.5" cy="15.5" r="5.5" />
  </svg>
);

const CheckIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const AlertIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M12 8v4" />
    <path d="M12 16h.01" />
  </svg>
);

const ChangePasswordPanel = () => {
  const [user, setUser] = useState(null);
  const [newpass, setNewpass] = useState("");
  const [confirmed, setConfirmed] = useState("");
  const [updateOk, setUpdateOk] = useState(false);
  const [invalidPassword, setInvalidPassword] = useState(false);
  const [error, setError] = useState("");
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    const raw = localStorage.getItem("user");
    if (raw) setUser(JSON.parse(raw));
  }, []);

  const resetForm = () => {
    setNewpass("");
    setConfirmed("");
    setUpdateOk(false);
    setInvalidPassword(false);
    setError("");
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (newpass !== confirmed) {
      setInvalidPassword(true);
      setUpdateOk(false);
      setError("");
      return;
    }
    if (!user?.id) return;
    setUpdating(true);
    setUpdateOk(false);
    setError("");
    try {
      const res = await patchUser(user.id, { password: confirmed });
      if (res && res.status >= 200 && res.status < 300) {
        setUpdateOk(true);
        setInvalidPassword(false);
        setNewpass("");
        setConfirmed("");
      } else {
        const errorResponse = handleApiError(res, "Couldn't update password", {
          notify: false,
        });
        setError(errorResponse.message);
      }
    } finally {
      setUpdating(false);
    }
  };

  return (
    <ProfileCard as="form" onSubmit={changePassword}>
      <ProfileCardHead>
        <ProfileCardIcon>
          <KeyIcon />
        </ProfileCardIcon>
        <div>
          <ProfileCardTitle>Change password</ProfileCardTitle>
          <ProfileCardDesc>
            Choose a strong password and confirm it below. We&apos;ll update
            your credentials immediately.
          </ProfileCardDesc>
        </div>
      </ProfileCardHead>

      {updateOk ? (
        <ProfileAlert>
          <CheckIcon />
          Password updated successfully.
        </ProfileAlert>
      ) : null}
      {invalidPassword ? (
        <ProfileAlert $variant="error">
          <AlertIcon />
          Passwords do not match. Please try again.
        </ProfileAlert>
      ) : null}
      {error ? (
        <ProfileAlert $variant="error">
          <AlertIcon />
          {error}
        </ProfileAlert>
      ) : null}

      <ProfileFields>
        <ProfileField>
          <ProfileLabel htmlFor="feNewPass">New password</ProfileLabel>
          <ProfileInput
            id="feNewPass"
            type="password"
            autoComplete="new-password"
            placeholder="Enter a new password"
            value={newpass}
            onChange={(e) => {
              setNewpass(e.target.value);
              setUpdateOk(false);
              setInvalidPassword(false);
              setError("");
            }}
          />
          <ProfileHint>Must be at least 8 characters.</ProfileHint>
        </ProfileField>
        <ProfileField>
          <ProfileLabel htmlFor="feConfirmPass">Confirm password</ProfileLabel>
          <ProfileInput
            id="feConfirmPass"
            type="password"
            autoComplete="new-password"
            placeholder="Re-enter your new password"
            value={confirmed}
            onChange={(e) => {
              setConfirmed(e.target.value);
              setUpdateOk(false);
              setInvalidPassword(false);
              setError("");
            }}
          />
        </ProfileField>
      </ProfileFields>

      <ProfileFooter>
        <ProfileGhostBtn type="button" onClick={resetForm} disabled={updating}>
          Cancel
        </ProfileGhostBtn>
        <ProfilePrimaryBtn type="submit" disabled={updating}>
          {updating ? "Updating…" : "Update password"}
        </ProfilePrimaryBtn>
      </ProfileFooter>
    </ProfileCard>
  );
};

export default ChangePasswordPanel;
